import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useExamContext } from "@/contexts/ExamContext";
import { useSpacedRepetition } from "@/hooks/useSpacedRepetition";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  ArrowLeft,
  Brain,
  RotateCw,
  Sparkles,
  ThumbsUp,
  ThumbsDown,
  Zap,
  CheckCircle2,
  Check,
  BookOpen,
  Undo2,
} from "lucide-react";
import { SEO } from "@/components/seo/SEO";
import { cn } from "@/lib/utils";
import { ExplanationBody } from "@/components/practice/ExplanationBody";
import { ExplanationSearchBar } from "@/components/practice/ExplanationSearchBar";
import { parseExplanation } from "@/lib/explanationFormat";

const sb = supabase as any;

const OPTION_LETTERS = ["A", "B", "C", "D", "E"] as const;

type Flashcard = {
  id: string;
  stem: string;
  options: string[];
  correct_letter: string;
  correct_text: string;
  correct_index: number;
  explanation: string | null;
  fromSrs: boolean;
};

const SESSION_SIZE = 20;


export default function Flashcards() {
  const { user } = useAuth();
  const { selectedExamSlug } = useExamContext();
  const { dueItems, scheduleReview, completeReview, isLoading: srsLoading } = useSpacedRepetition();
  const prefersReducedMotion = useReducedMotion();

  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [stats, setStats] = useState({ again: 0, good: 0, easy: 0 });

  const { data: pool = [], isLoading: poolLoading } = useQuery({
    queryKey: ["flashcard-pool", user?.id, selectedExamSlug, dueItems.map((i) => i.mcq_id).join(",")],
    enabled: !!user?.id && !srsLoading,
    staleTime: 0,
    queryFn: async () => {
      const cards: Flashcard[] = [];
      const seen = new Set<string>();

      const dueIds = dueItems.map((i) => i.mcq_id);
      if (dueIds.length > 0) {
        const { data } = await sb
          .from("mcqs")
          .select("id, stem, option_a, option_b, option_c, option_d, option_e, correct_option, explanation")
          .in("id", dueIds.slice(0, SESSION_SIZE));
        (data || []).forEach((m: any) => {
          if (seen.has(m.id)) return;
          seen.add(m.id);
          cards.push(toFlashcard(m, true));
        });
      }

      const remaining = SESSION_SIZE - cards.length;
      if (remaining > 0) {
        let q = sb
          .from("mcqs")
          .select(`
            id, stem, option_a, option_b, option_c, option_d, option_e, correct_option, explanation,
            mcq_exam_labels!inner(label:exam_labels!inner(slug))
          `)
          .eq("lifecycle_status", "active");

        if (selectedExamSlug) {
          q = q.eq("mcq_exam_labels.label.slug", selectedExamSlug);
        }

        const { data } = await q.limit(remaining * 4);
        const fresh = (data || [])
          .filter((m: any) => !seen.has(m.id))
          .sort(() => Math.random() - 0.5)
          .slice(0, remaining);

        fresh.forEach((m: any) => {
          seen.add(m.id);
          cards.push(toFlashcard(m, false));
        });
      }

      return cards;
    },
  });

  const isLoading = srsLoading || poolLoading;
  const total = pool.length;
  const current = pool[index];
  const finished = !isLoading && total > 0 && index >= total;
  const progress = total > 0 ? Math.min(100, (index / total) * 100) : 0;

  useEffect(() => {
    setIndex(0);
    setFlipped(false);
    setStats({ again: 0, good: 0, easy: 0 });
  }, [pool.length]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (finished || !current) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return;
      if (e.code === "Space") {
        e.preventDefault();
        setFlipped((f) => !f);
      }
      if (!flipped) return;
      if (e.key === "1") rate("again");
      if (e.key === "2") rate("good");
      if (e.key === "3") rate("easy");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, flipped, finished]);

  const rate = (rating: "again" | "good" | "easy") => {
    if (!current) return;
    const wasCorrect = rating !== "again";

    if (current.fromSrs) {
      completeReview({ mcqId: current.id, wasCorrect });
    } else if (rating === "again" || rating === "good") {
      scheduleReview(current.id);
    }

    setStats((s) => ({ ...s, [rating]: s[rating] + 1 }));
    setFlipped(false);
    setIndex((i) => i + 1);
  };

  return (
    <>
      <SEO
        title="Flashcards — Spaced Repetition Practice"
        description="Quick flip-card review that schedules questions into your spaced repetition queue."
      />
      <div className="container mx-auto max-w-3xl py-6 space-y-6 px-4">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/dashboard">
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Link>
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Brain className="h-6 w-6 text-primary" />
                Flashcards
              </h1>
              <p className="text-sm text-muted-foreground">
                Flip the card, rate your recall, build long-term memory.
              </p>
            </div>
          </div>
        </div>

        {isLoading ? (
          <Card>
            <CardContent className="py-16 text-center text-muted-foreground">Loading your deck…</CardContent>
          </Card>
        ) : total === 0 ? (
          <EmptyState />
        ) : finished ? (
          <FinishedSummary stats={stats} total={total} />
        ) : (
          <>
            {/* Slim progress with inline session stats */}
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3 text-xs">
                <span className="text-muted-foreground font-medium">
                  Card {index + 1} of {total}
                </span>
                <div className="flex items-center gap-3 text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <ThumbsDown className="h-3 w-3 text-destructive" /> {stats.again}
                  </span>
                  <span className="flex items-center gap-1">
                    <ThumbsUp className="h-3 w-3" /> {stats.good}
                  </span>
                  <span className="flex items-center gap-1">
                    <Zap className="h-3 w-3 text-primary" /> {stats.easy}
                  </span>
                </div>
              </div>
              <Progress value={progress} className="h-1.5" />
            </div>

            <FlipCard
              card={current!}
              flipped={flipped}
              onFlip={() => setFlipped((f) => !f)}
              prefersReducedMotion={prefersReducedMotion}
            />

            {!flipped ? (
              <div className="flex flex-col items-center gap-2">
                <Button
                  size="lg"
                  onClick={() => setFlipped(true)}
                  className="gap-2 bg-gradient-medical shadow-medical"
                >
                  <RotateCw className="h-4 w-4" /> Reveal Answer
                  <kbd className="ml-1 px-1.5 py-0.5 text-[10px] rounded bg-primary-foreground/20 text-primary-foreground/90">
                    Space
                  </kbd>
                </Button>
                <p className="text-xs text-muted-foreground">Tap the card or press Space</p>
              </div>
            ) : (
              <RatingBar onRate={rate} onFlipBack={() => setFlipped(false)} />
            )}
          </>
        )}
      </div>
    </>
  );
}

function toFlashcard(m: any, fromSrs: boolean): Flashcard {
  const opts = [m.option_a, m.option_b, m.option_c, m.option_d, m.option_e].filter(Boolean) as string[];
  const idx = OPTION_LETTERS.indexOf(m.correct_option);
  return {
    id: m.id,
    stem: m.stem,
    options: opts,
    correct_letter: m.correct_option,
    correct_text: opts[idx] ?? "",
    correct_index: idx,
    explanation: m.explanation,
    fromSrs,
  };
}

function FlipCard({
  card,
  flipped,
  onFlip,
  prefersReducedMotion,
}: {
  card: Flashcard;
  flipped: boolean;
  onFlip: () => void;
  prefersReducedMotion: boolean;
}) {
  return (
    <div
      className={cn(
        "relative w-full min-h-[460px] cursor-pointer group",
        !prefersReducedMotion && "transition-transform active:scale-[0.995]"
      )}
      onClick={onFlip}
      role="button"
      tabIndex={0}
      aria-pressed={flipped}
      aria-label={flipped ? "Showing answer. Click to show question." : "Showing question. Click to reveal answer."}
      onKeyDown={(e) => {
        if (e.key === "Enter") onFlip();
      }}
      style={{ perspective: "1400px" }}
    >
      <div
        className={cn(
          "relative w-full min-h-[460px]",
          !prefersReducedMotion && "transition-transform duration-500",
          flipped && !prefersReducedMotion && "[transform:rotateY(180deg)]"
        )}
        style={{ transformStyle: "preserve-3d" }}
      >
        {/* Front */}
        <div
          className={cn(
            "absolute inset-0",
            prefersReducedMotion && flipped && "hidden"
          )}
          style={{ backfaceVisibility: "hidden" }}
        >
          <FlashcardFront card={card} />
        </div>

        {/* Back */}
        <div
          className={cn(
            "absolute inset-0",
            prefersReducedMotion && !flipped && "hidden"
          )}
          style={{
            backfaceVisibility: "hidden",
            transform: prefersReducedMotion ? undefined : "rotateY(180deg)",
          }}
        >
          <FlashcardBack card={card} />
        </div>
      </div>
    </div>
  );
}

function FlashcardFront({ card }: { card: Flashcard }) {
  return (
    <Card className="medical-card border-primary/20 h-full transition-shadow group-hover:shadow-feature">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 font-semibold">
            <Sparkles className="h-3.5 w-3.5 text-primary" /> Question
          </CardTitle>
          {card.fromSrs ? (
            <Badge className="bg-gradient-medical text-primary-foreground border-0 text-[10px] h-5">
              SRS Review
            </Badge>
          ) : (
            <Badge variant="outline" className="text-[10px] h-5">
              New
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-foreground whitespace-pre-wrap leading-relaxed text-[15px]">{card.stem}</p>
        <div className="space-y-2">
          {card.options.map((opt, i) => (
            <div
              key={i}
              className="flex items-start gap-3 rounded-lg border border-border/60 bg-muted/30 px-3 py-2.5"
            >
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-background border border-border flex items-center justify-center text-xs font-semibold text-foreground">
                {OPTION_LETTERS[i]}
              </span>
              <span className="text-sm text-foreground/90 leading-relaxed">{opt}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function FlashcardBack({ card }: { card: Flashcard }) {
  const hasExplanation = !!(card.explanation && card.explanation.trim().length > 0);
  const [searchQuery, setSearchQuery] = useState("");
  const [matchCount, setMatchCount] = useState(0);
  const showSearch =
    hasExplanation &&
    parseExplanation(card.explanation as string).totalLength >= 120;

  // Reset search when card changes.
  useEffect(() => {
    setSearchQuery("");
  }, [card.id]);

  return (
    <Card className="medical-card border-success/30 h-full overflow-hidden flex flex-col">
      {/* Success banner */}
      <div className="bg-gradient-to-r from-success/15 via-success/10 to-transparent border-b border-success/20 px-5 py-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-success/20 flex items-center justify-center">
            <CheckCircle2 className="h-5 w-5 text-success" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-success/80 mb-0.5">
              Correct Answer
            </div>
            <div className="font-semibold text-foreground leading-snug">
              <span className="text-success mr-1.5">{card.correct_letter}.</span>
              {card.correct_text}
            </div>
          </div>
        </div>
      </div>

      <CardContent className="flex-1 overflow-y-auto pt-5 space-y-5">
        {/* Options recap */}
        <div className="space-y-1.5">
          {card.options.map((opt, i) => {
            const isCorrect = i === card.correct_index;
            return (
              <div
                key={i}
                className={cn(
                  "flex items-start gap-3 rounded-lg px-3 py-2 transition-colors border",
                  isCorrect
                    ? "border-success/40 bg-success/10 border-l-4 border-l-success"
                    : "border-transparent bg-muted/20 opacity-60"
                )}
              >
                <span
                  className={cn(
                    "flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold",
                    isCorrect
                      ? "bg-success text-success-foreground"
                      : "bg-background border border-border text-muted-foreground"
                  )}
                >
                  {isCorrect ? <Check className="h-3.5 w-3.5" /> : OPTION_LETTERS[i]}
                </span>
                <span
                  className={cn(
                    "text-sm leading-relaxed",
                    isCorrect ? "text-foreground font-medium" : "text-muted-foreground"
                  )}
                >
                  {opt}
                </span>
              </div>
            );
          })}
        </div>

        {/* Explanation */}
        <div className="space-y-3 pt-2 border-t border-border/60">
          <div className="flex items-center gap-1.5 pt-3">
            <BookOpen className="h-3.5 w-3.5 text-primary" />
            <h3 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
              Explanation
            </h3>
          </div>
          {hasExplanation ? (
            <>
              {showSearch && (
                <ExplanationSearchBar
                  value={searchQuery}
                  onChange={setSearchQuery}
                  matchCount={matchCount}
                />
              )}
              <ExplanationBody
                text={card.explanation as string}
                mcqId={card.id}
                source="flashcard"
                compact
                searchQuery={searchQuery}
                onMatchCountChange={setMatchCount}
              />
            </>
          ) : (
            <p className="text-sm italic text-muted-foreground">
              No additional explanation provided for this question.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function RatingBar({
  onRate,
  onFlipBack,
}: {
  onRate: (r: "again" | "good" | "easy") => void;
  onFlipBack: () => void;
}) {
  return (
    <div className="space-y-3">
      <p className="text-center text-xs text-muted-foreground">
        How well did you recall this? Your rating schedules the next review.
      </p>
      <div className="grid grid-cols-3 gap-2">
        <RatingButton
          onClick={() => onRate("again")}
          variant="again"
          icon={<ThumbsDown className="h-4 w-4" />}
          label="Again"
          hint="< 1 day"
          shortcut="1"
        />
        <RatingButton
          onClick={() => onRate("good")}
          variant="good"
          icon={<ThumbsUp className="h-4 w-4" />}
          label="Good"
          hint="1–7 days"
          shortcut="2"
        />
        <RatingButton
          onClick={() => onRate("easy")}
          variant="easy"
          icon={<Zap className="h-4 w-4" />}
          label="Easy"
          hint="Skip"
          shortcut="3"
        />
      </div>
      <div className="flex justify-center">
        <Button variant="ghost" size="sm" onClick={onFlipBack} className="text-muted-foreground gap-1.5">
          <Undo2 className="h-3.5 w-3.5" /> Flip back to question
        </Button>
      </div>
    </div>
  );
}

function RatingButton({
  onClick,
  variant,
  icon,
  label,
  hint,
  shortcut,
}: {
  onClick: () => void;
  variant: "again" | "good" | "easy";
  icon: React.ReactNode;
  label: string;
  hint: string;
  shortcut: string;
}) {
  const styles: Record<typeof variant, string> = {
    again:
      "border-destructive/30 bg-destructive/5 text-destructive hover:bg-destructive/10 hover:border-destructive/50",
    good: "border-border bg-card text-foreground hover:bg-muted",
    easy:
      "border-transparent bg-gradient-medical text-primary-foreground hover:opacity-95 shadow-medical",
  };

  return (
    <button
      onClick={onClick}
      aria-label={`${label} — review interval ${hint}, keyboard shortcut ${shortcut}`}
      className={cn(
        "group relative flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 px-3 py-3.5 text-sm font-semibold transition-all",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        "active:scale-[0.97]",
        styles[variant]
      )}
    >
      <div className="flex items-center gap-1.5">
        {icon}
        <span>{label}</span>
      </div>
      <div className="flex items-center gap-1.5 text-[10px] font-normal opacity-80">
        <span>{hint}</span>
        <kbd
          className={cn(
            "px-1.5 py-0.5 rounded text-[9px] font-mono",
            variant === "easy" ? "bg-primary-foreground/20" : "bg-muted"
          )}
        >
          {shortcut}
        </kbd>
      </div>
    </button>
  );
}

function EmptyState() {
  return (
    <Card className="medical-card">
      <CardContent className="py-16 text-center space-y-3">
        <Brain className="h-12 w-12 mx-auto text-muted-foreground/40" />
        <h3 className="font-semibold">No flashcards available</h3>
        <p className="text-sm text-muted-foreground">
          Practice some questions first — incorrect answers feed your flashcard deck automatically.
        </p>
        <div className="flex justify-center gap-2 pt-2">
          <Button asChild>
            <Link to="/practice">Start Practicing</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/study">Browse Topics</Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function FinishedSummary({
  stats,
  total,
}: {
  stats: { again: number; good: number; easy: number };
  total: number;
}) {
  const strongRatio = (stats.good + stats.easy) / Math.max(total, 1);
  const encouragement =
    strongRatio >= 0.8
      ? "Strong session — most cards landed in long-term memory."
      : strongRatio >= 0.5
        ? "Solid progress. Keep cycling through to reinforce the trickier ones."
        : "Tough deck today — those 'Again' cards will resurface soon to lock them in.";

  return (
    <Card className="medical-card border-primary/30">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-primary" /> Session complete
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-muted-foreground text-sm">
          You reviewed {total} card{total !== 1 ? "s" : ""}. {encouragement}
        </p>
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-lg border bg-destructive/5 border-destructive/20 p-3 text-center">
            <div className="text-2xl font-bold text-destructive">{stats.again}</div>
            <div className="text-xs text-muted-foreground">Again</div>
          </div>
          <div className="rounded-lg border bg-secondary p-3 text-center">
            <div className="text-2xl font-bold">{stats.good}</div>
            <div className="text-xs text-muted-foreground">Good</div>
          </div>
          <div className="rounded-lg border border-primary/30 bg-primary/10 p-3 text-center">
            <div className="text-2xl font-bold text-primary">{stats.easy}</div>
            <div className="text-xs text-muted-foreground">Easy</div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button asChild className="flex-1 bg-gradient-medical">
            <Link to="/flashcards" reloadDocument>
              Start New Session
            </Link>
          </Button>
          <Button asChild variant="outline" className="flex-1">
            <Link to="/dashboard">Back to Dashboard</Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
